import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { categoryMeta } from "@/mocks/videos";

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setActive((prev) => (prev + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => setActive((prev) => (prev + dir + items.length) % items.length);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-primary-950" aria-label="焦点推荐">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <img
            src={item.banner || item.cover}
            alt={`${item.title}焦点推荐`}
            title={`${item.title} - 69视频推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-4">
              <i className="ri-fire-fill" aria-hidden="true"></i>
              本期焦点
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-background-50 leading-tight mb-3">
              {items[active].title}
            </h2>
            <div className="flex items-center gap-3 text-sm text-background-100/90 mb-4 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-background-50/15 border border-background-100/20">
                {categoryMeta[items[active].type].label}
              </span>
              <span>{items[active].year}</span>
              <span className="flex items-center gap-1 text-accent-400 font-semibold">
                <i className="ri-star-fill" aria-hidden="true"></i>
                {items[active].rating.toFixed(1)}
              </span>
              <span className="hidden sm:inline">{items[active].duration}</span>
            </div>
            <p className="text-sm md:text-base text-background-100/80 leading-relaxed line-clamp-2 md:line-clamp-3 mb-6 max-w-xl">
              {items[active].summary}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/detail/${items[active].id}`}
                className="px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-information-line" aria-hidden="true"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${items[active].type}`}
                className="px-6 py-3 rounded-md border border-background-100/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{categoryMeta[items[active].type].label}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-4">
        <div className="flex items-center gap-2">
          {items.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(idx)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${idx === active ? "w-8 bg-accent-500" : "w-4 bg-background-100/50 hover:bg-background-100/80"}`}
              aria-label={`切换到第${idx + 1}项`}
            ></button>
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-background-100/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            aria-label="上一项"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-background-100/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            aria-label="下一项"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </section>
  );
}